// Libs
import classNames from 'classnames/bind';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BasePagination, BaseStatus, Loading } from '~/components/common';
// Others
import { formatDate } from '~/utils/helper/common';
import { DateFormatEnum } from '~/utils/enum/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IQueryParamBase } from '~/utils/interface/common';
import { IPaginationResponse } from '~/utils/interface/base';
import { IOutstandingSiteVisit } from '~/utils/interface/statistic';
import { getStatisticOutstandingSiteVisit } from '~/thunks/statistic/statisticThunk';
import { DEFAULT_CURRENT_PAGE, DEFAULT_LIMIT_PAGE_DASHBOARD } from '~/utils/constants/common';
// Styles, images, icons
import styles from './OutstandingSiteVisit.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const OutstandingSiteVisit = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/dashboard', 'common/base']);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paramObject, setParamObject] = useState<IQueryParamBase>({
    page: DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_PAGE_DASHBOARD,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [listData, setListData] = useState<IOutstandingSiteVisit[]>([]);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListOutstandingSiteVisit(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListOutstandingSiteVisit = (params: IQueryParamBase) => {
    setIsLoading(true);

    dispatch(getStatisticOutstandingSiteVisit(params))
      .unwrap()
      .then((res) => {
        const { responses: listData, pagination } = res.data;

        if (!listData) return;

        setListData(listData);
        setPagination(pagination);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
  };
  //#endregion Handle Function

  return (
    <Box id='outstandingSiteVisitComponent' className={cx('container')}>
      <Stack direction={'row'} spacing={'13px'} justifyContent={'space-between'} justifyItems={'center'}>
        <Typography className={cx('title')}>{t('dashboard_title_outstanding_site_visit')}</Typography>

        <Stack className={cx('viewStatusTitle')}>
          <Typography className={cx('statusTitle')}>
            {t('overbooking_title_pending', {
              value: listData.length,
            })}
          </Typography>
        </Stack>
      </Stack>

      <Stack className={cx('content')}>
        {listData && listData.length > 0 ? (
          listData.map((item, index) => (
            <Stack key={item?.id} className={cx('itemTask', index < listData.length - 1 && 'border')}>
              <Stack direction={'column'} gap={1}>
                <Typography className={cx('titleItem')}>{item?.title}</Typography>

                <Typography className={cx('titleSub')}>{item?.type}</Typography>

                <Stack direction={'row'} gap={2} alignItems={'center'}>
                  <img
                    src={icons.commonIconCalendar}
                    alt={t('altImg', {
                      ns: 'common/base',
                    })}
                    className={cx('iconDate')}
                  />

                  <Typography className={cx('dateItem')}>
                    {formatDate(item?.date, DateFormatEnum.MMM_D_YYYY)}
                  </Typography>
                </Stack>
              </Stack>

              <BaseStatus label={item.status} type='big' />
            </Stack>
          ))
        ) : (
          <Typography align='center' className={cx('noData')}>
            {t('common_no_data_available', { ns: 'common/base' })}
          </Typography>
        )}

        <BasePagination count={pagination?.totalPages} page={pagination?.page} onChange={handlePageChange} />

        {isLoading && <Loading />}
      </Stack>
    </Box>
  );
};

export default OutstandingSiteVisit;
